import { useState, useMemo, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@paulownia/shared/components/ui/dialog';
import { Button } from '@paulownia/shared/components/ui/button';
import { Loader2, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import type { Product, SaleMode, ProductOption, PortionSize, WeightEstimate, ServingEstimate, CompositionComponent } from '@paulownia/shared/types';
import type { ProcessedImages } from '@paulownia/shared';
import {
  WizardProgress,
  WizardStepMode,
  WizardStepPricing,
  WizardStepDetails,
  WizardStepOptions,
  WizardStepComposition,
} from './wizard';

const WIZARD_STEPS = [
  { id: 'mode', label: 'Mode' },
  { id: 'pricing', label: 'Prix' },
  { id: 'details', label: 'Détails' },
  { id: 'options', label: 'Options' },
  { id: 'composition', label: 'Coffret' },
];

interface ProductWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (product: Product, pendingImage: ProcessedImages | null) => Promise<void>;
  sections: string[];
  subSectionsBySection: Record<string, string[]>;
  existingOptions: ProductOption[];
  allProducts: Product[];
}

interface WizardFormData {
  saleMode?: SaleMode;
  price?: number;
  weightEstimate?: WeightEstimate;
  servingEstimate?: ServingEstimate;
  portionSizes?: PortionSize[];
  disclaimer?: string;
  name: string;
  shortenName?: string;
  description?: string;
  picture?: string;
  pendingImage: ProcessedImages | null;
  section: string;
  subSection?: string;
  availableOptions: ProductOption[];
  compositionEnabled: boolean;
  compositionTotalRequired: number;
  compositionComponents: CompositionComponent[];
}

// Helper to generate camelCase ID from label
function toCamelCase(str: string): string {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+(.)/g, (_, chr) => chr.toUpperCase())
    .replace(/[^a-zA-Z0-9]/g, '')
    .replace(/^./, (chr) => chr.toLowerCase());
}

export function ProductWizard({
  isOpen,
  onClose,
  onSave,
  sections,
  subSectionsBySection,
  existingOptions,
  allProducts,
}: ProductWizardProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<WizardFormData>({
    name: '',
    section: '',
    pendingImage: null,
    availableOptions: [],
    compositionEnabled: false,
    compositionTotalRequired: 1,
    compositionComponents: [],
  });

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
      setFormData({
        name: '',
        section: '',
        pendingImage: null,
        availableOptions: [],
        compositionEnabled: false,
        compositionTotalRequired: 1,
        compositionComponents: [],
      });
    }
  }, [isOpen]);

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose();
    }
  };

  // Field updaters
  const updateField = <K extends keyof WizardFormData>(field: K, value: WizardFormData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Validation for each step
  const isStepValid = useMemo(() => {
    switch (currentStep) {
      case 0: // Mode
        return !!formData.saleMode;

      case 1: // Pricing
        if (!formData.price || formData.price <= 0) return false;
        if (formData.saleMode === 'UNIT_PRICED_BY_WEIGHT') {
          if (!formData.weightEstimate?.averageWeight || formData.weightEstimate.averageWeight <= 0) {
            return false;
          }
        }
        if (formData.saleMode === 'WEIGHT_ESTIMATED') {
          if (!formData.servingEstimate?.gramsPerPerson || formData.servingEstimate.gramsPerPerson <= 0) {
            return false;
          }
        }
        if (formData.saleMode === 'WEIGHT_PACKAGED') {
          if (!formData.portionSizes || formData.portionSizes.length === 0) {
            return false;
          }
          // Check that at least one portion size has a label
          if (!formData.portionSizes.some(p => p.label.trim())) {
            return false;
          }
        }
        return true;

      case 2: // Details
        return !!formData.name?.trim() && !!formData.section?.trim();

      case 3: // Options
        return true; // Options are optional

      case 4: // Composition
        if (formData.compositionEnabled) {
          return formData.compositionTotalRequired > 0 && formData.compositionComponents.length > 0;
        }
        return true; // Optional if not enabled

      default:
        return false;
    }
  }, [currentStep, formData]);

  // Navigation
  const goToNext = () => {
    if (currentStep < WIZARD_STEPS.length - 1 && isStepValid) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const goToPrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const goToStep = (stepIndex: number) => {
    // Only allow going back to completed steps
    if (stepIndex < currentStep) {
      setCurrentStep(stepIndex);
    }
  };

  // Handle save
  const handleSave = async () => {
    if (!isStepValid) return;
    // saleMode is required at step 1, so it must be defined here
    if (!formData.saleMode) return;

    // Clean up portion sizes and generate readable IDs
    const validPortionSizes = (formData.portionSizes || [])
      .filter(p => p.label.trim())
      .map(p => ({
        id: toCamelCase(p.label) || p.id,
        label: p.label.trim(),
        price: p.price,
      }));

    // Build composition option if enabled
    const compositionOption: ProductOption | undefined = formData.compositionEnabled
      ? {
          id: 'composition',
          label: 'Composition du coffret',
          type: 'composition',
          totalRequired: formData.compositionTotalRequired,
          components: formData.compositionComponents,
        }
      : undefined;

    // Combine regular options with composition option
    const allOptions = [
      ...formData.availableOptions,
      ...(compositionOption ? [compositionOption] : []),
    ];

    const product: Product = {
      id: '', // Will be generated by Firestore
      name: formData.name.trim(),
      shortenName: formData.shortenName?.trim() || undefined,
      description: formData.description?.trim() || undefined,
      picture: formData.picture?.trim() || undefined,
      section: formData.section.trim().toLowerCase(),
      subSection: formData.subSection?.trim().toLowerCase() || undefined,
      // Sale mode fields
      saleMode: formData.saleMode,
      price: formData.price,
      weightEstimate: formData.weightEstimate,
      servingEstimate: formData.servingEstimate,
      disclaimer: formData.disclaimer,
      portionSizes: validPortionSizes.length > 0 ? validPortionSizes : undefined,
      availableOptions: allOptions.length > 0 ? allOptions : undefined,
    };

    setSaving(true);
    try {
      await onSave(product, formData.pendingImage);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const isLastStep = currentStep === WIZARD_STEPS.length - 1;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0 pb-4 border-b">
          <DialogTitle>Nouveau produit</DialogTitle>
          <div className="pt-4">
            <WizardProgress
              steps={WIZARD_STEPS}
              currentStep={currentStep}
              onStepClick={goToStep}
            />
          </div>
        </DialogHeader>

        {/* Step Content */}
        <div className="flex-1 overflow-y-auto py-4 px-1">
          {currentStep === 0 && (
            <WizardStepMode
              saleMode={formData.saleMode}
              onSaleModeChange={(mode) => updateField('saleMode', mode)}
            />
          )}

          {currentStep === 1 && formData.saleMode && (
            <WizardStepPricing
              saleMode={formData.saleMode}
              price={formData.price}
              weightEstimate={formData.weightEstimate}
              servingEstimate={formData.servingEstimate}
              portionSizes={formData.portionSizes}
              disclaimer={formData.disclaimer}
              onPriceChange={(price) => updateField('price', price)}
              onWeightEstimateChange={(estimate) => updateField('weightEstimate', estimate)}
              onServingEstimateChange={(estimate) => updateField('servingEstimate', estimate)}
              onPortionSizesChange={(sizes) => updateField('portionSizes', sizes)}
              onDisclaimerChange={(disclaimer) => updateField('disclaimer', disclaimer)}
            />
          )}

          {currentStep === 2 && (
            <WizardStepDetails
              name={formData.name}
              shortenName={formData.shortenName}
              description={formData.description}
              picture={formData.picture}
              pendingImage={formData.pendingImage}
              section={formData.section}
              subSection={formData.subSection}
              sections={sections}
              subSectionsBySection={subSectionsBySection}
              onNameChange={(name) => updateField('name', name)}
              onShortenNameChange={(shortenName) => updateField('shortenName', shortenName)}
              onDescriptionChange={(description) => updateField('description', description)}
              onPictureChange={(picture) => updateField('picture', picture)}
              onPendingImageChange={(pendingImage) => updateField('pendingImage', pendingImage)}
              onSectionChange={(section) => updateField('section', section)}
              onSubSectionChange={(subSection) => updateField('subSection', subSection)}
            />
          )}

          {currentStep === 3 && (
            <WizardStepOptions
              availableOptions={formData.availableOptions}
              existingOptions={existingOptions}
              onOptionsChange={(options) => updateField('availableOptions', options)}
            />
          )}

          {currentStep === 4 && (
            <WizardStepComposition
              enabled={formData.compositionEnabled}
              onEnabledChange={(enabled) => updateField('compositionEnabled', enabled)}
              totalRequired={formData.compositionTotalRequired}
              onTotalRequiredChange={(total) => updateField('compositionTotalRequired', total)}
              components={formData.compositionComponents}
              onComponentsChange={(components) => updateField('compositionComponents', components)}
              allProducts={allProducts}
            />
          )}
        </div>

        {/* Footer */}
        <DialogFooter className="flex-shrink-0 pt-4 border-t gap-2">
          <div className="flex items-center justify-between w-full gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={currentStep === 0 ? onClose : goToPrevious}
              disabled={saving}
            >
              {currentStep === 0 ? (
                'Annuler'
              ) : (
                <>
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Précédent
                </>
              )}
            </Button>

            {isLastStep ? (
              <Button
                type="button"
                onClick={handleSave}
                disabled={!isStepValid || saving}
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Check className="h-4 w-4 mr-1" />
                )}
                Créer le produit
              </Button>
            ) : (
              <Button
                type="button"
                onClick={goToNext}
                disabled={!isStepValid}
              >
                Suivant
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
